<template>
  <section :class="$style.screen">
    <header :class="$style.header">
      <div :class="$style.headerTop">
        <div :class="$style.brand">
          <div :class="$style.logo">
            <img v-if="department?.logo" :src="department.logo" alt="" />
            <div v-else :class="$style.code">{{ department?.short_code }}</div>
          </div>
          <div :class="$style.brandInfo">
            <h2 :class="$style.departmentTitle">
              {{ department?.title || "—" }}
            </h2>
            <p :class="$style.departmentAddress">
              {{ department?.address || "—" }}
            </p>
          </div>
        </div>

        <button
          type="button"
          :class="$style.close"
          aria-label="Закрыть"
          @click="$emit('close')"
        >
          <img src="/figma/success/success-close.svg" alt="" />
        </button>
      </div>

      <div :class="$style.contacts">
        <a
          v-if="department?.phone_number"
          :href="`tel:${department.phone_number}`"
          :class="$style.phone"
        >
          {{ department.phone_number }}
        </a>

        <div v-if="socialLinks.length" :class="$style.socials">
          <a
            v-for="item in socialLinks"
            :key="item.key"
            :href="item.url"
            target="_blank"
            rel="noopener noreferrer"
            :class="$style.social"
            :aria-label="item.label"
          >
            <img :src="item.icon" :class="$style[`icon-${item.key}`]" alt="" />
          </a>
        </div>
      </div>
    </header>

    <div :class="$style.body">
      <div :class="$style.info">
        <div :class="$style.successIcon">
          <img src="/figma/success/success-check.svg" alt="" />
        </div>

        <div :class="$style.infoText">
          <h1 :class="$style.title">
            {{ isQueueMode ? "Заявка создана!" : "Вы записаны!" }}
          </h1>
          <p :class="$style.subtitle">
            {{
              isQueueMode
                ? "Ожидайте вызова в живой очереди"
                : "Ждем вас в указанное время"
            }}
          </p>
        </div>
      </div>

      <div :class="$style.detailsSection">
        <button
          type="button"
          :class="[
            $style.detailsToggle,
            { [$style.detailsToggleOpen]: isDetailsOpen },
          ]"
          @click="toggleDetails"
        >
          <span>{{ isQueueMode ? "Детали заявки" : "Детали записи" }}</span>
          <img
            v-if="isDetailsOpen"
            src="/figma/success/success-close.svg"
            alt=""
          />
        </button>

        <div v-if="isDetailsOpen" :class="$style.detailsContent">
          <article :class="$style.card">
            <div v-if="!isQueueMode" :class="$style.cardHead">
              <div :class="[$style.dateBox, $style.dateBoxStart]">
                <img src="/figma/success/success-calendar.svg" alt="" />
                <span>{{ formattedDate }}</span>
              </div>
              <div :class="[$style.dateBox, $style.dateBoxEnd]">
                <img src="/figma/success/success-clock.svg" alt="" />
                <span>{{ formattedTime }}</span>
              </div>
            </div>

            <div :class="$style.vehicle">
              <div :class="$style.vehicleTitle">
                <img
                  v-if="carLogo"
                  :src="carLogo"
                  alt=""
                  :class="$style.vehicleImage"
                />
                <div
                  v-else
                  :class="$style.vehiclePlaceholder"
                  aria-hidden="true"
                />
                <span>{{ vehicleTitle }}</span>
              </div>
              <div v-if="plateNumber" :class="$style.plate">
                {{ plateNumber }}
              </div>
            </div>

            <div :class="$style.tags">
              <span
                v-for="label in tireLabels"
                :key="label"
                :class="$style.tireTag"
              >
                {{ label }}
              </span>
              <span :class="$style.statusTag">Заявка</span>
            </div>

            <ul v-if="serviceItems.length" :class="$style.services">
              <li
                v-for="item in serviceItems"
                :key="item.key"
                :class="$style.serviceItem"
              >
                <div :class="$style.serviceInfo">
                  <span>{{ item.title }}</span>
                  <div v-if="item.metaTags.length" :class="$style.serviceMeta">
                    <div
                      v-for="tag in item.metaTags"
                      :key="`${item.key}-${tag.label}`"
                      :class="$style.serviceMetaItem"
                    >
                      <div
                        :class="[
                          $style.serviceMetaDecor,
                          $style[tag.decorClass],
                        ]"
                      />
                      <div>{{ tag.label }}</div>
                    </div>
                  </div>
                </div>
                <span>{{ item.quantity }}X</span>
              </li>
            </ul>

            <div :class="$style.total">
              <span>ИТОГО</span>
              <span>{{ totalPrice }}</span>
            </div>

            <div v-if="order?.comment" :class="$style.comment">
              <img src="/figma/success/success-comment.svg" alt="" />
              <span>{{ order.comment }}</span>
            </div>
          </article>

          <div :class="$style.secondaryActions">
            <button
              type="button"
              :class="$style.secondaryButton"
              @click="downloadOrder"
            >
              <span>Поделиться</span>
              <img src="/figma/success/success-share.svg" alt="" />
            </button>

            <button
              type="button"
              :class="$style.secondaryButton"
              @click="downloadOrder"
            >
              <span>Сохранить</span>
              <img src="/figma/success/success-download.svg" alt="" />
            </button>
          </div>

          <p :class="$style.disclaimer">
            Сумма заказа рассчитывается автоматически с учетом указанных
            параметров и услуг.
          </p>
        </div>
      </div>
    </div>

    <footer :class="$style.footer">
      <div :class="$style.actions">
        <button type="button" :class="$style.done" @click="$emit('close')">
          Готово
        </button>
      </div>

      <div :class="$style.copyright">
        <span>© {{ year }} Все права защищены</span>
        <span>Шиномонтажный калькулятор</span>
      </div>
    </footer>
  </section>
</template>

<script>
import { formatDateRu, formatTime } from "@/utils/date";
import { mapState } from "vuex";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.addVirtualFileSystem(pdfFonts);

export default {
  name: "SuccessScreen",
  props: {
    order: {
      type: Object,
      default: null,
    },
    isQueueMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDetailsOpen: false,
    };
  },
  computed: {
    ...mapState({
      department: (state) => state.department.department,
      draftCar: (state) => state.orders.car,
      draftOrder: (state) => state.orders.order,
    }),
    year() {
      return new Date().getFullYear();
    },
    socialLinks() {
      const department = this.department || {};
      return [
        {
          key: "vk",
          label: "VK",
          icon: "/figma/step1-vk.svg",
          url: this.normalizeVkUrl(department.vk_link),
        },
        {
          key: "telegram",
          label: "Telegram",
          icon: "/figma/step1-tg.svg",
          url: this.normalizeTelegramUrl(department.telegram_link),
        },
        {
          key: "whatsapp",
          label: "WhatsApp",
          icon: "/figma/step1-wa.svg",
          url: this.normalizeWhatsAppUrl(department.whatsapp_link),
        },
        {
          key: "max",
          label: "Max",
          icon: "/figma/step1-max.svg",
          url: this.normalizeSocialUrl(department.max_link),
        },
      ].filter((item) => item.url);
    },
    formattedDate() {
      return this.order?.planned_at
        ? formatDateRu(this.order.planned_at, "dd.MM")
        : "—";
    },
    formattedTime() {
      return this.order?.planned_at ? formatTime(this.order.planned_at) : "—";
    },
    carLogo() {
      return this.order?.car?.make?.logo || "";
    },
    vehicleTitle() {
      const draftTitle = [
        this.draftCar?.make,
        this.draftCar?.model,
        this.draftCar?.generation,
      ]
        .filter(this.isMeaningfulText)
        .join(" ");

      const orderTitle = [
        this.extractEntityTitle(this.order?.car?.make),
        this.extractEntityTitle(this.order?.car?.model),
        this.extractEntityTitle(this.order?.car?.generation),
      ]
        .filter(Boolean)
        .join(" ");

      return draftTitle || orderTitle || "—";
    },
    plateNumber() {
      return (
        this.order?.car?.number ||
        this.draftOrder?.car?.number ||
        ""
      ).toUpperCase();
    },
    tireLabels() {
      const firstTire =
        this.normalizeTireTitle(
          this.order?.car?.tires?.summer_tires?.first_tires ||
            this.order?.car?.tires?.first_tires
        ) || this.draftCar?.first_tires;

      const secondTire =
        this.normalizeTireTitle(
          this.order?.car?.tires?.summer_tires?.second_tires ||
            this.order?.car?.tires?.second_tires
        ) || this.draftCar?.second_tires;

      return [firstTire, secondTire].filter(
        (item, index, items) =>
          this.isMeaningfulText(item) && items.indexOf(item) === index
      );
    },
    serviceItems() {
      const offers = Array.isArray(this.order?.offers) ? this.order.offers : [];
      return offers
        .map((item, index) => ({
          key: item.id || item.offer?.id || index,
          title: item.offer?.title || item.title || "",
          quantity: item.wheels_quan || item.quantity || 1,
          metaTags: [
            item.include_runflat
              ? { label: "Runflat", decorClass: "serviceMetaDecorDefault" }
              : null,
            item.include_pressure_sensor
              ? { label: "Датчик", decorClass: "serviceMetaDecorRed" }
              : null,
            item.include_alloy_wheel
              ? { label: "Литой", decorClass: "serviceMetaDecorGreen" }
              : null,
          ].filter(Boolean),
        }))
        .filter((item) => item.title);
    },
    totalPrice() {
      const raw =
        this.order?.price ??
        this.order?.net_price ??
        this.serviceItems.reduce((sum, item, index) => {
          const offer = this.order?.offers?.[index];
          return sum + Number(offer?.price || offer?.offer?.price || 0);
        }, 0);

      const amount = Number(raw || 0);
      return `${new Intl.NumberFormat("ru-RU", {
        maximumFractionDigits: 0,
      }).format(Math.round(amount))}${this.order?.currency?.symbol || "₽"}`;
    },
    shareText() {
      const lines = [
        "Детали записи",
        `Дата: ${this.formattedDate}`,
        `Время: ${this.formattedTime}`,
        `Адрес: ${this.department?.address || "—"}`,
        `Автомобиль: ${this.vehicleTitle}`,
      ];

      if (this.plateNumber) {
        lines.push(`Номер: ${this.plateNumber}`);
      }

      if (this.tireLabels.length) {
        lines.push(`Типоразмер: ${this.tireLabels.join(", ")}`);
      }

      if (this.serviceItems.length) {
        lines.push(
          ...this.serviceItems.map((item) => {
            const tags = item.metaTags.map((tag) => tag.label).join(", ");
            return tags
              ? `${item.title}: ${item.quantity}X (${tags})`
              : `${item.title}: ${item.quantity}X`;
          })
        );
      }

      lines.push(`Итого: ${this.totalPrice}`);

      if (this.order?.comment) {
        lines.push(`Комментарий: ${this.order.comment}`);
      }

      return lines.join("\n");
    },
  },
  methods: {
    normalizeSocialUrl(value) {
      const normalized = String(value || "").trim();
      if (!normalized) {
        return "";
      }

      if (/^https?:\/\//i.test(normalized)) {
        return normalized;
      }

      return `https://${normalized.replace(/^\/+/, "")}`;
    },
    normalizeVkUrl(value) {
      const normalized = String(value || "").trim();
      if (!normalized) {
        return "";
      }

      if (/^https?:\/\//i.test(normalized)) {
        return normalized;
      }

      const username = normalized.replace(/^@/, "").replace(/^\/+/, "");
      if (!username) {
        return "";
      }

      return `https://vk.com/${username}`;
    },
    normalizeTelegramUrl(value) {
      const normalized = String(value || "").trim();
      if (!normalized) {
        return "";
      }

      if (/^https?:\/\//i.test(normalized)) {
        return normalized;
      }

      if (normalized.startsWith("@")) {
        return `https://t.me/${normalized.slice(1)}`;
      }

      return this.normalizeSocialUrl(normalized);
    },
    normalizeWhatsAppUrl(value) {
      const normalized = String(value || "").trim();
      if (!normalized) {
        return "";
      }

      if (/^https?:\/\//i.test(normalized)) {
        return normalized;
      }

      const digits = normalized.replace(/\D/g, "");
      if (digits) {
        return `https://wa.me/${digits}`;
      }

      return this.normalizeSocialUrl(normalized);
    },
    isMeaningfulText(value) {
      if (typeof value !== "string") return false;
      const normalized = value.trim();
      if (!normalized) return false;
      return !/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(normalized);
    },
    extractEntityTitle(value) {
      if (value && typeof value === "object") {
        return value.title || "";
      }

      return this.isMeaningfulText(value) ? value.trim() : "";
    },
    normalizeTireTitle(value) {
      if (value && typeof value === "object") {
        return value.title || "";
      }

      return this.isMeaningfulText(value) ? value.trim() : "";
    },
    toggleDetails() {
      this.isDetailsOpen = !this.isDetailsOpen;
    },
    async shareOrder() {
      if (navigator.share) {
        await navigator.share({
          title: "Детали записи",
          text: this.shareText,
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(this.shareText);
      }
    },
    buildPdfDefinition() {
      const detailRows = [
        ["Дата", this.formattedDate],
        ["Время", this.formattedTime],
        ["Адрес", this.department?.address || "—"],
        ["Автомобиль", this.vehicleTitle],
      ];

      if (this.plateNumber) {
        detailRows.push(["Номер", this.plateNumber]);
      }

      if (this.tireLabels.length) {
        detailRows.push(["Типоразмер", this.tireLabels.join(", ")]);
      }

      const services = this.serviceItems.length
        ? [
            {
              text: "Услуги",
              style: "sectionTitle",
              margin: [0, 0, 0, 8],
            },
            {
              stack: this.serviceItems.flatMap((item, index) => {
                const content = [
                  {
                    columns: [
                      {
                        text: item.title,
                        style: "serviceTitle",
                      },
                      {
                        text: `${item.quantity}X`,
                        style: "serviceQty",
                        alignment: "right",
                        width: "auto",
                      },
                    ],
                  },
                ];

                if (item.metaTags.length) {
                  content.push({
                    text: item.metaTags.map((tag) => tag.label).join(" • "),
                    style: "serviceMeta",
                    margin: [0, 4, 0, 0],
                  });
                }

                if (index < this.serviceItems.length - 1) {
                  content.push({
                    canvas: [
                      {
                        type: "line",
                        x1: 0,
                        y1: 0,
                        x2: 515,
                        y2: 0,
                        lineWidth: 1,
                        lineColor: "#E5E7EB",
                      },
                    ],
                    margin: [0, 10, 0, 10],
                  });
                }

                return content;
              }),
              margin: [0, 0, 0, 16],
            },
          ]
        : [];

      const comment = this.order?.comment
        ? [
            {
              text: "Комментарий",
              style: "sectionTitle",
              margin: [0, 0, 0, 8],
            },
            {
              text: this.order.comment,
              margin: [0, 0, 0, 16],
            },
          ]
        : [];

      return {
        pageSize: "A4",
        pageMargins: [40, 48, 40, 48],
        defaultStyle: {
          font: "Roboto",
          fontSize: 11,
          color: "#2C2C2C",
        },
        content: [
          {
            text: this.department?.title || "Детали записи",
            style: "header",
          },
          {
            text: this.department?.address || "",
            style: "subheader",
            margin: [0, 4, 0, 24],
          },
          {
            text: "Вы записаны!",
            style: "title",
          },
          {
            text: "Ждем вас в указанное время",
            style: "subtitle",
            margin: [0, 4, 0, 24],
          },
          {
            table: {
              widths: [120, "*"],
              body: detailRows.map(([label, value]) => [
                { text: label, style: "labelCell" },
                { text: value || "—", style: "valueCell" },
              ]),
            },
            layout: {
              hLineColor: () => "#E5E7EB",
              vLineColor: () => "#E5E7EB",
              paddingLeft: () => 10,
              paddingRight: () => 10,
              paddingTop: () => 8,
              paddingBottom: () => 8,
            },
            margin: [0, 0, 0, 16],
          },
          ...services,
          {
            text: `Итого: ${this.totalPrice}`,
            style: "total",
            margin: [0, 0, 0, comment.length ? 16 : 0],
          },
          ...comment,
        ],
        styles: {
          header: {
            fontSize: 16,
            bold: true,
          },
          subheader: {
            fontSize: 10,
            color: "#6B7280",
          },
          title: {
            fontSize: 24,
            bold: true,
          },
          subtitle: {
            fontSize: 12,
            color: "#6B7280",
          },
          sectionTitle: {
            fontSize: 12,
            bold: true,
          },
          serviceTitle: {
            fontSize: 11,
            bold: true,
            color: "#2C2C2C",
          },
          serviceQty: {
            fontSize: 11,
            bold: true,
            color: "#2C2C2C",
          },
          serviceMeta: {
            fontSize: 9,
            color: "#6B7280",
          },
          labelCell: {
            bold: true,
            color: "#6B7280",
          },
          valueCell: {
            color: "#2C2C2C",
          },
          total: {
            fontSize: 14,
            bold: true,
          },
        },
      };
    },
    async downloadOrder() {
      const documentDefinition = this.buildPdfDefinition();
      const filename = `order-${this.order?.id || "success"}.pdf`;
      await pdfMake.createPdf(documentDefinition).download(filename);
    },
  },
};
</script>

<style lang="scss" module>
.screen {
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background-color: $bg-white;
  overflow: hidden;
}

.header {
  flex-shrink: 0;
  padding: 1.5rem;
  background-color: $bg-white;
  @include tablet {
    padding: 1rem;
  }
}

.headerTop {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.brand {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  min-width: 0;
}

.logo {
  width: 3.5rem;
  height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .code {
    border-radius: 0.5rem;
    background-color: $bg-white;
    border: 1px solid $bg-separator;
    width: 3.5rem;
    height: 3.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    font-weight: 600;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 0.5rem;
  }
}

.brandInfo {
  min-width: 0;
}

.departmentTitle {
  margin: 0;
  color: #010101;
  font-size: 1.3125rem;
  font-weight: 600;
  line-height: 1.4;
}

.departmentAddress {
  margin: 0.25rem 0 0;
  color: #2c2c2c;
  font-size: 0.875rem;
  line-height: 1.4;
}

.close {
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    display: block;
  }
}

.contacts {
  margin-top: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  overflow: hidden;
}

.phone {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 2.5rem;
  padding: 0.5rem 1rem;
  border-radius: 6.25rem;
  background-color: $bg-white;
  border: 1px solid $t-primary;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  white-space: nowrap;
  flex-shrink: 0;
}

.socials {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.social {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: $bg-white;
  color: $t-secondary;
  font-size: 0.625rem;
  font-weight: 700;
  border: 1px solid $t-primary;
  flex-shrink: 0;

  img {
    object-fit: contain;
    display: block;
  }
}

.icon-vk,
.icon-whatsapp,
.icon-max {
  width: 1.25rem;
  height: 1.25rem;
}

.icon-telegram {
  width: 1.2275rem;
  height: 1rem;
}

.body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.75rem 1.5rem 1.5rem;
  background-color: $bg-white;
  overflow-y: auto;
  @include scrollbarY;
  @include tablet {
    padding: 1rem;
  }
}

.info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
}

.successIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  border-radius: 6.25rem;
  background-color: $green;

  img {
    width: 2rem;
    height: 2rem;
    display: block;
  }
}

.infoText {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: center;
}

.title {
  margin: 0;
  color: #2c2c2c;
  font-size: 2.25rem;
  font-weight: 600;
  line-height: 1.2;
}

.subtitle {
  margin: 0;
  color: $t-secondary;
  font-size: 0.875rem;
  line-height: 1.4;
}

.detailsSection {
  margin-top: 0.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.detailsToggle {
  width: fit-content;
  max-width: 100%;
  min-height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid $bg-secondary;
  border-radius: 0.25rem;
  background-color: $bg-white;
  color: #2c2c2c;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;

  img {
    width: 1.5rem;
    height: 1.5rem;
    display: block;
  }
}

.detailsToggleOpen {
  position: relative;
  width: 100%;
  justify-content: space-between;
  padding: 0;
  border: none;
  border-radius: 0;
  background-color: transparent;
  text-align: center;

  span {
    width: 100%;
    text-align: center;
  }

  img {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
}

.detailsContent {
  margin-top: 1rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.card {
  border: 1px solid $bg-secondary;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: $bg-white;
}

.cardHead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
}

.dateBox {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  img {
    width: 1.5rem;
    height: 1.5rem;
    display: block;
  }

  span {
    color: #2c2c2c;
    font-size: 2.25rem;
    font-weight: 600;
    line-height: 1.2;
    @include tablet {
      font-size: 1.5rem;
    }
  }
}

.dateBoxStart {
  justify-content: flex-start;
}

.dateBoxEnd {
  justify-content: flex-end;
  text-align: right;
}

.vehicle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 1rem 1rem;
  @include custom(460) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.vehicleTitle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;

  img {
    display: block;
  }

  span {
    color: #2c2c2c;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.2;
  }
}

.vehicleImage,
.vehiclePlaceholder {
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border-radius: 50%;
}

.vehicleImage {
  object-fit: cover;
}

.vehiclePlaceholder {
  border: 1px solid $bg-secondary;
  background-color: $bg-primary;
}

.plate {
  flex-shrink: 0;
  padding: 0.5rem 1rem;
  border: 1px solid #2c2c2c;
  border-radius: 0.125rem;
  color: #2c2c2c;
  font-size: 0.75rem;
  line-height: 1.3;
  text-transform: uppercase;
}

.tags {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0 1rem 1rem;
}

.tireTag {
  min-height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.75rem;
  border-radius: 6.25rem;
  background-color: #2c2c2c;
  color: $t-white;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.2;
}

.statusTag {
  margin-left: auto;
  min-height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border-radius: 0.1875rem;
  background-color: $blue;
  color: #2c2c2c;
  font-size: 1rem;
  line-height: 1.5;
}

.services {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.serviceItem {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 1rem;
  background-color: $bg-white;

  span:last-child {
    color: #2c2c2c;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.2;
    flex-shrink: 0;
  }
}

.serviceInfo {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;

  span {
    color: #2c2c2c;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.2;
  }
}

.serviceMeta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.serviceMetaItem {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: $t-secondary;
  font-size: 0.75rem;
  line-height: 1.3;
}

.serviceMetaDecor {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background-color: #2c2c2c;
  flex-shrink: 0;
}

.serviceMetaDecorDefault {
  background-color: #2c2c2c;
}

.serviceMetaDecorRed {
  background-color: #ff4d4f;
}

.serviceMetaDecorGreen {
  background-color: #34c759;
}

.total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 1rem 1rem;

  span {
    color: #2c2c2c;
    font-size: 1.3125rem;
    font-weight: 600;
    line-height: 1.4;
  }
}

.comment {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: $blue;

  img {
    width: 1.5rem;
    height: 1.5rem;
    display: block;
    flex-shrink: 0;
  }

  span {
    color: #1d1d1d;
    font-size: 0.75rem;
    line-height: 1.3;
  }
}

.secondaryActions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  @include custom(390) {
    grid-template-columns: 1fr;
  }
}

.secondaryButton {
  min-height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid $bg-secondary;
  border-radius: 0.25rem;
  background-color: $bg-white;
  color: #2c2c2c;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;

  img {
    width: 1.5rem;
    height: 1.5rem;
    display: block;
  }
}

.disclaimer {
  margin: 0;
  color: $t-secondary;
  font-size: 0.75rem;
  line-height: 1.3;
  text-align: center;
}

.footer {
  flex-shrink: 0;
  margin-top: auto;
  padding: 1rem 1.5rem;
  background-color: $bg-white;
  border-top: 1px solid $bg-secondary;
  @include tablet {
    padding: 1rem;
  }
}

.actions {
  display: grid;
}

.done {
  min-height: 2.5rem;
  border: none;
  border-radius: 0.25rem;
  background-color: $green;
  color: $t-white;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
}

.copyright {
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: $t-tertiary;
  font-size: 0.75rem;
  line-height: 1.3;
  text-align: center;

  span:last-child {
    text-decoration: underline;
  }
}
</style>
